/**
 * This is the selector of competitor products
 */

import type { ProteinCalcProduct } from '@bulknutrients/bulk-shared-lib/interfaces/ProteinCalculator';
import { CustomImage } from '@components/Media/Image';
import Image from 'next/legacy/image';
import {
  type Dispatch,
  type KeyboardEvent,
  type ReactElement,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';
import { InputField } from './InputField';
import { SelectField } from './SelectField';

export function ComparisonSelect({
  brands,
  setComparisonProduct,
}: {
  brands: { brand: string; products: ProteinCalcProduct[] }[];
  setComparisonProduct: Dispatch<
    SetStateAction<ProteinCalcProduct | undefined>
  >;
}): ReactElement {
  const [brand, setBrand] = useState<{
    brand: string;
    products: ProteinCalcProduct[];
  }>();
  const [product, setProduct] = useState<ProteinCalcProduct>();
  const [size, setSize] = useState('');
  const [price, setPrice] = useState(0);
  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [sizeOptions, setSizeOptions] = useState<any[]>([]);

  const brandOptions = Array.from(new Set(brands.map(({ brand }) => brand)));

  // Set the product and size options when branch, product changes
  useEffect(() => {
    if (brand) {
      setProductOptions(brand.products);

      if (product)
        setSizeOptions(
          brand.products
            .filter(({ sku }) => sku === product.sku)
            .map((p) => p.displayWeight)
        );
    }
  }, [brand, product, brands]);

  // Default the options when brand changes
  useEffect(() => {
    setProduct(undefined);
    setSize('');
    setPrice(0);
    setComparisonProduct(undefined);
  }, [brand, setComparisonProduct]);

  // Default the options when Product changes
  useEffect(() => {
    setSize('');
    setPrice(0);
    setComparisonProduct(undefined);
  }, [brand, setComparisonProduct]);

  // If size or price changes, set the selected comparison product
  useEffect(() => {
    if (brand && product) {
      const prod = brands
        .filter((p) => p.brand === brand?.brand)[0]
        .products.filter(
          (p) => p.displayWeight === size && p.sku === product?.sku
        )[0];

      if (prod && price > 0) {
        prod.price = price * 100;
        setComparisonProduct({ ...prod });
      } else {
        setComparisonProduct(undefined);
      }
    }
  }, [size, price]);

  return (
    <form
      data-testid="comparison-select"
      className="min-w-[160px] grow p-[2vmin] text-center"
    >
      <CustomImage
        src="/images/protein-tub.webp"
        alt="Protein tub"
        width={140}
        height={160}
        className="dark:invert"
      />
      <h4 className="m-0 mb-2 text-center text-lg font-medium text-orange">
        Select Your Current Product From Below:
      </h4>
      <div className="flex flex-wrap">
        <div className="shrink-0 grow-0 basis-full px-[calc(0.5*16px)] py-0">
          <SelectField
            options={brandOptions}
            label="Brand"
            value={brand?.brand || ''}
            update={(newBrand) =>
              setBrand(brands.filter(({ brand }) => brand === newBrand)[0])
            }
          />
        </div>
        <div className="shrink-0 grow-0 basis-full px-[calc(0.5*16px)] py-0">
          <SelectField
            options={productOptions.map((p) => p.name)}
            label="Product"
            disabled={!brand}
            value={product?.name || ''}
            update={(p) =>
              setProduct(productOptions.filter(({ name }) => name === p)[0])
            }
          />
        </div>
        <div className="shrink-0 grow-0 basis-full px-[calc(0.5*16px)] py-0">
          <SelectField
            options={sizeOptions}
            label="Size"
            disabled={!product || !brand}
            value={size}
            update={setSize}
          />
        </div>
        <div className="shrink-0 grow-0 basis-full px-[calc(0.5*16px)] py-0">
          <InputField
            type="number"
            label="Price you paid ($)"
            errorMessage="Please enter a price"
            disabled={!product || !brand || !size}
            value={price}
            update={setPrice}
            min={0}
            max={200}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
              e.key === 'Enter' && e.preventDefault()
            }
          />
        </div>
      </div>
    </form>
  );
}
